import React, { useState, useEffect } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, PhotoIcon, ArrowDownIcon, ArrowUpIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import ProjectModal from '../../components/admin/ProjectModal';
import { Project } from '../../types/project';
import { projectService } from '../../services/projectService';

type SortField = 'title' | 'status' | 'location' | 'squareFeet' | 'completionDate' | 'featured';
type SortDirection = 'asc' | 'desc';

const Projects = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isViewingGallery, setIsViewingGallery] = useState(false);
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [galleryTitle, setGalleryTitle] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('title');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setIsLoading(true);
      const data = await projectService.getAllProjects();
      setProjects(data);
      setError(null);
    } catch (err) {
      setError('Failed to load projects. Please try again.');
      console.error('Error loading projects:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (project: Project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (projectId: string) => {
    setDeleteConfirmId(projectId);
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmId) {
      try {
        await projectService.deleteProject(deleteConfirmId);
        setProjects(projects.filter((p) => p.id !== deleteConfirmId));
        setDeleteConfirmId(null);
      } catch (err) {
        setError('Failed to delete project. Please try again.');
        console.error('Error deleting project:', err);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmId(null);
  };

  const handleSave = async (projectData: Omit<Project, 'id'>) => {
    try {
      if (selectedProject) {
        // Edit existing project
        await projectService.updateProject(selectedProject.id, projectData);
        setProjects(projects.map((p) =>
          p.id === selectedProject.id ? { ...p, ...projectData } : p
        ));
      } else {
        // Add new project
        const newProjectId = await projectService.addProject(projectData);
        const newProject: Project = {
          ...projectData,
          id: newProjectId,
        };
        setProjects([...projects, newProject]);
      }
      setError(null);
    } catch (err) {
      setError('Failed to save project. Please try again.');
      console.error('Error saving project:', err);
    }
  };

  const openGallery = (project: Project) => {
    if (project.images && project.images.length > 0) {
      setGalleryImages(project.images);
      setGalleryTitle(project.title);
      setIsViewingGallery(true);
    }
  };

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (field !== sortField) return null;
    
    return sortDirection === 'asc' 
      ? <ArrowUpIcon className="h-4 w-4 ml-1" /> 
      : <ArrowDownIcon className="h-4 w-4 ml-1" />;
  };

  const sortedProjects = [...projects]
    .filter(project => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        project.title.toLowerCase().includes(query) ||
        project.location.toLowerCase().includes(query) ||
        project.status.toLowerCase().includes(query) ||
        project.description?.toLowerCase().includes(query)
      );
    })
    .sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];
      
      // Special handling for numeric fields stored as strings
      if (sortField === 'squareFeet') {
        aValue = parseInt(a.squareFeet) || 0;
        bValue = parseInt(b.squareFeet) || 0;
      }
      
      if (sortField === 'completionDate') {
        aValue = new Date(a.completionDate).getTime();
        bValue = new Date(b.completionDate).getTime();
      }
      
      // Compare the values
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-primary-500">Loading projects...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {isViewingGallery ? (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-primary-900">{galleryTitle} - Images</h2>
            <button
              onClick={() => setIsViewingGallery(false)}
              className="px-3 py-1 rounded-md border border-primary-300 text-primary-700 hover:bg-primary-50"
            >
              Back to Projects
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {galleryImages.map((image, index) => (
              <div key={index} className="relative rounded-lg overflow-hidden shadow-md group">
                <img 
                  src={image} 
                  alt={`${galleryTitle} image ${index + 1}`} 
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <a 
                    href={image} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="px-3 py-1 bg-white text-primary-900 rounded-md hover:bg-primary-50"
                  >
                    View Full Size
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
          <div className="sm:flex sm:items-center">
            <div className="sm:flex-auto">
              <h1 className="text-xl font-semibold text-primary-900">Projects</h1>
              <p className="mt-2 text-sm text-primary-700">
                Manage your project portfolio. Add, edit, or remove projects as needed.
              </p>
            </div>
            <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
              <button
                type="button"
                onClick={() => {
                  setSelectedProject(null);
                  setIsModalOpen(true);
                }}
                className="inline-flex items-center justify-center rounded-md border border-transparent bg-accent-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-2 sm:w-auto"
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                Add Project
              </button>
            </div>
          </div>

          <div className="mt-4 bg-white rounded-lg shadow mb-4 p-4">
            <div className="flex flex-col md:flex-row justify-between items-center mb-2">
              <div className="text-sm text-primary-500 mb-2 md:mb-0">
                {sortedProjects.length} {sortedProjects.length === 1 ? 'project' : 'projects'} total
              </div>
              <div className="relative rounded-md">
                <input
                  type="text"
                  className="block w-full rounded-md border-primary-300 pr-10 focus:border-accent-500 focus:ring-accent-500 sm:text-sm"
                  placeholder="Search projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg className="h-5 w-5 text-primary-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-col">
            <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-primary-300">
                    <thead className="bg-primary-50">
                      <tr>
                        <th
                          scope="col"
                          onClick={() => handleSort('title')}
                          className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-primary-900 sm:pl-6 cursor-pointer hover:bg-primary-100"
                        >
                          <div className="flex items-center">
                            Title
                            {getSortIcon('title')}
                          </div>
                        </th>
                        <th
                          scope="col"
                          onClick={() => handleSort('status')}
                          className="px-3 py-3.5 text-left text-sm font-semibold text-primary-900 cursor-pointer hover:bg-primary-100"
                        >
                          <div className="flex items-center">
                            Status
                            {getSortIcon('status')}
                          </div>
                        </th>
                        <th
                          scope="col"
                          onClick={() => handleSort('location')}
                          className="px-3 py-3.5 text-left text-sm font-semibold text-primary-900 cursor-pointer hover:bg-primary-100"
                        >
                          <div className="flex items-center">
                            Location
                            {getSortIcon('location')}
                          </div>
                        </th>
                        <th
                          scope="col"
                          onClick={() => handleSort('squareFeet')}
                          className="px-3 py-3.5 text-left text-sm font-semibold text-primary-900 cursor-pointer hover:bg-primary-100"
                        >
                          <div className="flex items-center">
                            Square Feet
                            {getSortIcon('squareFeet')}
                          </div>
                        </th>
                        <th
                          scope="col"
                          onClick={() => handleSort('completionDate')}
                          className="px-3 py-3.5 text-left text-sm font-semibold text-primary-900 cursor-pointer hover:bg-primary-100"
                        >
                          <div className="flex items-center">
                            Completion Date
                            {getSortIcon('completionDate')}
                          </div>
                        </th>
                        <th
                          scope="col"
                          onClick={() => handleSort('featured')}
                          className="px-3 py-3.5 text-left text-sm font-semibold text-primary-900 cursor-pointer hover:bg-primary-100"
                        >
                          <div className="flex items-center">
                            Featured
                            {getSortIcon('featured')}
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-primary-900"
                        >
                          Images
                        </th>
                        <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-primary-200 bg-white">
                      {sortedProjects.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="py-10 text-center text-sm text-primary-500">
                            {searchQuery ? (
                              <div>
                                <p className="mb-1">No projects found matching "{searchQuery}"</p>
                                <button 
                                  onClick={() => setSearchQuery('')}
                                  className="text-accent-600 hover:text-accent-500"
                                >
                                  Clear search
                                </button>
                              </div>
                            ) : (
                              <div>
                                <p className="mb-1">No projects available</p>
                                <button 
                                  onClick={() => {
                                    setSelectedProject(null);
                                    setIsModalOpen(true);
                                  }}
                                  className="text-accent-600 hover:text-accent-500"
                                >
                                  Add your first project
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ) : (
                        sortedProjects.map((project) => (
                          <tr key={project.id} className="hover:bg-primary-50">
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-primary-900 sm:pl-6">
                              {project.title}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-primary-500">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                project.status === 'Completed' 
                                  ? 'bg-green-100 text-green-800' 
                                  : project.status === 'In Progress' 
                                  ? 'bg-blue-100 text-blue-800' 
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {project.status}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-primary-500">
                              {project.location}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-primary-500">
                              {project.squareFeet}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-primary-500">
                              {new Date(project.completionDate).toLocaleDateString()}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-primary-500">
                              <span className={`inline-flex rounded-full h-2 w-2 mr-1 ${project.featured ? 'bg-green-500' : 'bg-primary-300'}`}></span>
                              {project.featured ? 'Yes' : 'No'}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-primary-500">
                              {project.images && project.images.length > 0 ? (
                                <div className="flex items-center">
                                  <div className="flex -space-x-2 overflow-hidden">
                                    {project.images.slice(0, 3).map((image, idx) => (
                                      <img 
                                        key={idx}
                                        src={image} 
                                        alt="" 
                                        className="inline-block h-8 w-8 rounded-full ring-2 ring-white object-cover"
                                      />
                                    ))}
                                  </div>
                                  {project.images.length > 3 && (
                                    <span className="ml-1 text-xs text-primary-400">+{project.images.length - 3} more</span>
                                  )}
                                  <button
                                    onClick={() => openGallery(project)}
                                    className="ml-2 text-accent-600 hover:text-accent-700"
                                  >
                                    <PhotoIcon className="h-5 w-5" aria-hidden="true" />
                                  </button>
                                </div>
                              ) : (
                                <span className="italic text-primary-400">No images</span>
                              )}
                            </td>
                            <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                              <button
                                onClick={() => handleEdit(project)}
                                className="text-accent-600 hover:text-accent-900 mr-4"
                              >
                                <PencilIcon className="h-5 w-5" aria-hidden="true" />
                              </button>
                              <button
                                onClick={() => handleDeleteClick(project.id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                <TrashIcon className="h-5 w-5" aria-hidden="true" />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-auto">
            <div className="flex items-center text-amber-600 mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 mr-2" />
              <h3 className="text-lg font-medium text-primary-900">Confirm Deletion</h3>
            </div>
            <p className="mb-4 text-primary-700">
              Are you sure you want to delete this project? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleDeleteCancel}
                className="px-4 py-2 border border-primary-300 rounded-md text-primary-700 hover:bg-primary-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <ProjectModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        project={selectedProject}
        onSave={handleSave}
      />
    </div>
  );
};

export default Projects; 